import { gridHeaderIconsSx } from '@/components/ReportDashboard/Shared/GridActionsMenu'
import { COLOR_PALETTE, COMPANY_COLORS } from '@/constants/colors.constant'
import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import {
  useAttributionDateRange,
  useAttributionFactorContributionFreq,
  useAttributionFactorContributionTotal,
  useAttributionFactorFreqError,
  useAttributionLoading,
  useAttributionPeriodLabel,
  useAttributionPortfolioName,
  useAttributionReportId,
  useAttributionZoom,
} from '@/contexts/FAAttributionDataContext/hooks'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { cardPrefsContextKey, CardProps } from '@/interfaces/raDashboard.interface'
import { putUserPreference } from '@/services/userPreference.service'
import {
  buildDrillableGroups,
  buildFactorAttributionChart,
  buildFactorAttributionTotals,
  buildFactorAttributionTreeRows,
  buildFactorDrillTotals,
  buildFactorPeriods,
  FactorAttributionTreeRow,
  factorDisplayName,
  FactorFrequency,
  factorSelectionRange,
  OTHER_GROUP_NAME,
  pickFactorFrequency,
} from '@/utils/factorAttributionUtils'
import { getGridThemeClass } from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import {
  ColDef,
  ColGroupDef,
  ColumnMovedEvent,
  GridReadyEvent,
  ITooltipParams,
  ModuleRegistry,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import BarChartIcon from '@mui/icons-material/BarChart'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowGroupingModule,
  MenuModule,
  CsvExportModule,
  ExcelExportModule,
])

// cwan brand palette for the stacked factor series.
const FACTOR_COLORS = COLOR_PALETTE

// Shared style for the clickable drill breadcrumb links.
const crumbLinkSx = {
  fontSize: '12px',
  color: COMPANY_COLORS.DARK_BLUE,
  background: 'none',
  border: 0,
  p: 0,
  cursor: 'pointer',
} as const

// Hover shows the full-precision value (cells round to 2dp, which can read as 0 for tiny numbers).
const fullValueTooltip = (p: ITooltipParams) => (p.value == null ? '' : String(p.value))
// Plain 2dp number: en dash when null, 0.00 for a genuine zero. Any unit lives in the column header.
const num2 = (p: ValueFormatterParams) => (p.value == null ? '–' : Number(p.value).toFixed(2))

// Display-only factor label (data keys unchanged): drops the Equity/FixInc prefix, shows Fundamental as Style.
const displayFactorName = factorDisplayName

type FactorAttributionView = 'chart' | 'table'

// Factor Attribution card: per-period factor contribution, stacked by factor.
const FactorAttributionChart: React.FC<CardProps> = (props) => {
  const { cardId } = props
  const theme = useTheme()
  const isDarkMode = useDarkModeCheck()
  const contextRows = useAttributionFactorContributionFreq()
  const totalRows = useAttributionFactorContributionTotal()
  const factorError = useAttributionFactorFreqError()
  const isLoading = useAttributionLoading()
  const reportId = useAttributionReportId()
  const portfolioName = useAttributionPortfolioName()
  const dateRange = useAttributionDateRange()
  const periodLabel = useAttributionPeriodLabel()
  const { zoomPeriod, setZoomPeriod, zoomLoading, zoomError } = useAttributionZoom()
  const title = CardTitleLookup[cardId]
  const [view, setView] = useState<FactorAttributionView>(() => {
    const saved = props.cardPrefs?.factorAttribution?.view
    return saved === 'chart' || saved === 'table' ? saved : 'chart'
  })
  // Drill path = successive factor clicks, each descending one level (Equity → Region → …).
  // Not persisted: drilling is relative to the current report/date range.
  const [drillPath, setDrillPath] = useState<string[]>([])
  // Top-strip toggle: top-level factors hidden from the (undrilled) chart.
  const [hiddenFactors, setHiddenFactors] = useState<Set<string>>(
    () => new Set(props.cardPrefs?.factorAttribution?.hiddenFactors ?? []),
  )

  // Re-sync view/hiddenFactors if cardPrefs resolves or changes after mount (the async load can
  // finish after the initial render's useState default already ran).
  useEffect(() => {
    const fa = props.cardPrefs?.factorAttribution
    if (!fa) return
    if (fa.view === 'chart' || fa.view === 'table') setView(fa.view)
    setHiddenFactors(new Set(fa.hiddenFactors ?? []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.cardPrefs])

  const saveViewPrefs = (newView: FactorAttributionView, hidden: Set<string>) => {
    if (props.cardPrefs?.dashboardName) {
      const newPrefs = {
        ...props.cardPrefs,
        factorAttribution: {
          ...props.cardPrefs.factorAttribution,
          view: newView,
          hiddenFactors: [...hidden],
        },
      }
      putUserPreference(cardPrefsContextKey(props.cardPrefs.dashboardName), newPrefs)
      props.onCardPrefsChange?.(newPrefs)
    }
  }

  // Suppresses onFactorTableColumnMoved's save while applyColumnState is (a)synchronously
  // re-emitting columnMoved events, so restoring the saved order doesn't immediately re-save it.
  // Same pattern as Holdings/PortfolioHoldings.tsx, FactorLens/*.
  const applyingColumnOrderRef = useRef(false)
  // Ref to the mounted grid (the table only exists while view === 'table' && tableRows.length),
  // so the effect below can reach it via `factorTableGridRef.current?.api` — that resolves to
  // undefined automatically once the grid unmounts, unlike a manually-cached GridApi reference.
  const factorTableGridRef = useRef<AgGridReact>(null)
  // Tracks the dashboardName we've already applied the saved column order for, so a genuine
  // dashboard switch re-applies but an unrelated sibling-card pref save doesn't.
  const columnOrderAppliedForRef = useRef<string | null>(null)

  const applyFactorTableColumnOrder = (api: GridReadyEvent['api'], order: string[]) => {
    applyingColumnOrderRef.current = true
    api.applyColumnState({ state: order.map((colId) => ({ colId })), applyOrder: true })
    // Defer the reset so async events from applyColumnState are still suppressed.
    setTimeout(() => { applyingColumnOrderRef.current = false }, 0)
  }

  // Restores the table's saved column order on mount.
  const onFactorTableGridReady = (e: GridReadyEvent) => {
    const order = props.cardPrefs?.factorAttribution?.columnOrder
    if (order?.length) {
      columnOrderAppliedForRef.current = props.cardPrefs?.dashboardName ?? null
      applyFactorTableColumnOrder(e.api, order)
    }
  }

  // Re-applies the saved order if cardPrefs resolves/changes while the grid is already mounted —
  // e.g. the user clicks into Table view before the async prefs GET has resolved, in which case
  // onFactorTableGridReady above ran with no saved order to apply and nothing would otherwise
  // retry it. Gated on dashboardName (not just cardPrefs identity) so it only re-applies on an
  // actual dashboard switch, not on every unrelated sibling-card pref save.
  useEffect(() => {
    const dashboardName = props.cardPrefs?.dashboardName
    const order = props.cardPrefs?.factorAttribution?.columnOrder
    const api = factorTableGridRef.current?.api
    if (order?.length && api && dashboardName !== columnOrderAppliedForRef.current) {
      columnOrderAppliedForRef.current = dashboardName ?? null
      applyFactorTableColumnOrder(api, order)
    }
  }, [props.cardPrefs])

  // Persists column order only (not width/pin/hide) — merged into the existing factorAttribution
  // prefs so it doesn't clobber the view/hiddenFactors saved by saveViewPrefs, and vice versa.
  const onFactorTableColumnMoved = (e: ColumnMovedEvent) => {
    if (applyingColumnOrderRef.current) return
    if (!e.finished || !props.cardPrefs?.dashboardName) return
    const columnOrder = e.api.getColumnState().map((s) => s.colId)
    const newPrefs = {
      ...props.cardPrefs,
      factorAttribution: { ...props.cardPrefs.factorAttribution, view, columnOrder },
    }
    putUserPreference(cardPrefsContextKey(props.cardPrefs.dashboardName), newPrefs)
    props.onCardPrefsChange?.(newPrefs)
  }

  // Zoom overrides the base range; frequency is chosen from whichever range is active (zoom is finer).
  const rangeStart = zoomPeriod ? zoomPeriod.start : (dateRange?.beginDate ?? null)
  const rangeEnd = zoomPeriod ? zoomPeriod.end : (dateRange?.endDate ?? null)
  const currentFrequency: FactorFrequency = rangeStart && rangeEnd ? pickFactorFrequency(rangeStart, rangeEnd) : 'D'

  // Export filename: portfolioName_beginDate_endDate over the active range (zoomed if zoomed).
  const exportFileBase = useMemo(() => {
    const name = (portfolioName?.trim() || 'factor-attribution').replace(/[\\/:*?"<>|]/g, '_')
    const range = rangeStart && rangeEnd ? `_${rangeStart.format('YYYYMMDD')}_${rangeEnd.format('YYYYMMDD')}` : ''
    return `${name}${range}`
  }, [portfolioName, rangeStart, rangeEnd])
  // Export the raw full-precision value, not the 2dp display format. For the tree's Factor column,
  // emit the full path so a leaf row keeps its hierarchy once parent rows are skipped.
  const rawValueCallback = useCallback((params: any) => {
    // Only CSV is flat and needs the path; Excel keeps its row-group outline, so leave its key alone.
    if (params?.type === 'csv' && params?.column?.getColId?.() === 'ag-Grid-AutoColumn') {
      return (params?.node?.data as FactorAttributionTreeRow | undefined)?.factorPath?.join(' > ') ?? params.value
    }
    const field = params?.column?.getColDef?.()?.field
    const data = params?.node?.data
    return field && data && data[field] !== undefined ? data[field] : params.value
  }, [])
  // Decided filename + raw full-precision values; columnGroups keeps the "Return Contribution (%)" tier.
  // CSV is flat, so skip parent rows (their aggregates would double-count a plain sum over the column).
  const defaultCsvExportParams = useMemo(
    () => ({
      fileName: `${exportFileBase}.csv`,
      processCellCallback: rawValueCallback,
      columnGroups: true,
      shouldRowBeSkipped: (p: any) => !!p.node?.group || !!p.node?.footer,
    }),
    [exportFileBase, rawValueCallback],
  )
  // Excel keeps the collapsible row-group outline, so parent rows stay (no double-count when collapsed).
  const defaultExcelExportParams = useMemo(
    () => ({ fileName: `${exportFileBase}.xlsx`, processCellCallback: rawValueCallback, columnGroups: true }),
    [exportFileBase, rawValueCallback],
  )
  // Right-click menu: the generic Export only (no copy — clipboard/range modules aren't registered here).
  const getContextMenuItems = useCallback(() => ['export'], [])

  // Reset the factor drill whenever the report or the selected range genuinely changes — but not
  // on the initial mount/first report load, which would otherwise race the async cardPrefs load
  // and wipe out a just-restored hiddenFactors preference (see prevResetKeyRef below).
  const rangeKey = dateRange ? `${dateRange.beginDate.valueOf()}-${dateRange.endDate.valueOf()}` : ''
  // Tracks the previous (reportId, rangeKey) pair; null while the report hasn't loaded yet, so
  // both the initial mount and the first real report arriving are treated as "not a change" —
  // only a report/range switch *after* one has already loaded triggers the reset below.
  const prevResetKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const key = reportId != null && rangeKey ? `${reportId}|${rangeKey}` : null
    const prevKey = prevResetKeyRef.current
    prevResetKeyRef.current = key
    if (prevKey === null || key === null || prevKey === key) return
    setDrillPath([])
    // Re-apply the saved preference rather than clearing to empty, so a genuine report/range
    // switch doesn't overwrite the user's persisted hidden-factors choice.
    setHiddenFactors(new Set(props.cardPrefs?.factorAttribution?.hiddenFactors ?? []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, rangeKey])

  // Authoritative per-group totals (no-freq) at the current drill level — used to rank/sort the drilled
  // chart and to label the legend, so selection, order, and numbers all agree.
  const drillGroupTotals = useMemo(() => buildFactorDrillTotals(totalRows, drillPath), [totalRows, drillPath])

  // Groups at the current level that actually have a child to drill into. Derived from the freq rows
  // (the same rows the bars come from) so drill-ability doesn't wait on the totals response.
  const drillableGroups = useMemo(() => buildDrillableGroups(contextRows, drillPath), [contextRows, drillPath])

  const { categories, series } = useMemo(
    () => buildFactorAttributionChart(contextRows, currentFrequency, drillPath, drillGroupTotals),
    [contextRows, currentFrequency, drillPath, drillGroupTotals],
  )
  const hasData = series.length > 0 && categories.length > 0

  // Period boundaries behind the x categories, for mapping a drag-selection to a date range.
  const periods = useMemo(() => buildFactorPeriods(contextRows), [contextRows])

  // Persistent strip: each top-level factor's total contribution over the selected period. Always the
  // main factors, so it stays put while the chart is drilled. Uses the no-frequency totals endpoint
  // (the backend's aggregate) rather than summing the per-period data.
  const totals = useMemo(() => buildFactorAttributionTotals(totalRows), [totalRows])

  // One top-level factor → colour map (keyed by name), so the strip swatch and the undrilled bar
  // always agree even though they're indexed off different responses (totals vs freq).
  const topLevelColors = useMemo(() => {
    const map = new Map<string, string>()
    totals.forEach((t, i) => map.set(t.name, FACTOR_COLORS[i % FACTOR_COLORS.length]))
    return map
  }, [totals])

  // Drilled legend totals: each sub-factor's total contribution over the range (from the no-freq
  // totals), keyed by the chart's series names. "Other" gets the sum of the rolled-up sub-factors.
  const legendTotals = useMemo(() => {
    const map = new Map<string, number | null>()
    if (drillPath.length === 0) return map
    const kept = new Set(series.map((s) => s.name).filter((n) => n !== OTHER_GROUP_NAME))
    kept.forEach((name) => map.set(name, drillGroupTotals.get(name) ?? null))
    if (series.some((s) => s.name === OTHER_GROUP_NAME)) {
      let other: number | null = null
      drillGroupTotals.forEach((v, k) => {
        if (!kept.has(k) && v != null) other = (other ?? 0) + v
      })
      map.set(OTHER_GROUP_NAME, other)
    }
    return map
  }, [drillPath, series, drillGroupTotals])

  const tableRows = useMemo(() => buildFactorAttributionTreeRows(totalRows), [totalRows])
  const getDataPath = useCallback((data: FactorAttributionTreeRow) => data.factorPath, [])
  const columnDefs = useMemo<(ColDef | ColGroupDef)[]>(
    () => [
      {
        headerName: 'Factor Return',
        field: 'factorReturn',
        valueFormatter: num2,
        tooltipValueGetter: fullValueTooltip,
        headerClass: 'fc-divider',
        cellClass: 'fc-divider',
      },
      {
        headerName: 'Exposure',
        headerClass: 'fc-divider',
        // No aggFunc: weighted exposure isn't additive across factors, so parent rows have no value (– like any null).
        children: [
          {
            headerName: 'Portfolio',
            field: 'portfolioExposure',
            valueFormatter: num2,
            tooltipValueGetter: fullValueTooltip,
          },
          {
            headerName: 'Benchmark',
            field: 'benchmarkExposure',
            valueFormatter: num2,
            tooltipValueGetter: fullValueTooltip,
          },
          {
            headerName: 'Active',
            field: 'exposureDifference',
            valueFormatter: num2,
            tooltipValueGetter: fullValueTooltip,
            headerClass: 'fc-divider',
            cellClass: 'fc-divider',
          },
        ],
      },
      {
        headerName: 'Return Contribution (%)',
        children: [
          {
            headerName: 'Portfolio',
            field: 'portfolioReturn',
            aggFunc: 'sum',
            valueFormatter: num2,
            tooltipValueGetter: fullValueTooltip,
          },
          {
            headerName: 'Benchmark',
            field: 'benchmarkReturn',
            aggFunc: 'sum',
            valueFormatter: num2,
            tooltipValueGetter: fullValueTooltip,
          },
          {
            headerName: 'Active',
            field: 'activeReturn',
            aggFunc: 'sum',
            valueFormatter: num2,
            tooltipValueGetter: fullValueTooltip,
          },
        ],
      },
    ],
    [],
  )
  const defaultColDef = useMemo<ColDef>(() => ({ sortable: true, resizable: true, flex: 1, minWidth: 120 }), [])
  // Tree-data group column: shows the factor hierarchy (path keys are already display names).
  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: 'Factor',
      minWidth: 220,
      pinned: 'left',
    }),
    [],
  )

  // Chart / table view toggle (top-right).
  const toggleButtonSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '6px',
    border: `1px solid ${active ? COMPANY_COLORS.DARK_BLUE : theme.border}`,
    backgroundColor: active ? 'rgba(10, 94, 178, 0.08)' : theme.cardBackground,
    color: active ? COMPANY_COLORS.DARK_BLUE : theme.textSecondary,
    cursor: 'pointer',
  })

  const chartOptions = useMemo(
    (): Highcharts.Options => ({
      chart: {
        type: 'column',
        zooming: { type: 'x' },
        backgroundColor: theme.cardBackground,
        style: { fontFamily: "'Noto Sans', sans-serif" },
        animation: false,
        spacing: [8, 8, 8, 8],
        events: {
          // Drag-select an x-range to zoom: map the selected categories to dates and refetch (finer freq).
          selection: function (event: Highcharts.SelectEventObject) {
            const axis = event.xAxis?.[0]
            if (!axis) return false
            const range = factorSelectionRange(periods, axis.min, axis.max)
            if (range) setZoomPeriod(range)
            return false
          },
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      // Drilled: show the sub-factors in a bottom legend (the top strip keeps the top-level factors).
      // Clicking a legend item drills deeper (see events.itemClick below).
      legend: {
        enabled: drillPath.length > 0,
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: { color: theme.textPrimary, fontSize: '12px', fontWeight: '400' },
        itemHoverStyle: { color: theme.textPrimary },
        labelFormatter: function (this: Highcharts.Point | Highcharts.Series) {
          const name = (this as Highcharts.Series).name
          const total = legendTotals.get(name)
          const label = displayFactorName(name)
          if (total == null) return label
          return `${label} : ${total >= 0 ? '+' : ''}${total.toFixed(2)}%`
        },
        // Clicking a legend item drills one level deeper (same as clicking its bar); never toggle
        // series visibility. "Other" isn't a real factor, so it doesn't drill.
        events: {
          itemClick: function (e: Highcharts.LegendItemClickEventObject) {
            const name = (e.legendItem as Highcharts.Series)?.name
            if (name && drillableGroups.has(name)) {
              setDrillPath((prev) => [...prev, name])
            }
            return false
          },
        },
      },
      xAxis: {
        categories,
        labels: { style: { color: theme.textPrimary, fontSize: '12px' } },
        lineColor: theme.border,
        tickColor: theme.border,
      },
      yAxis: {
        title: { text: undefined },
        labels: { format: '{value}%', style: { color: theme.textPrimary, fontSize: '12px' } },
        gridLineColor: theme.border,
        lineColor: theme.border,
        stackLabels: { enabled: false },
      },
      // Hover a bar to list factors; the hovered factor shows Benchmark + Active in a detached side box.
      tooltip: {
        shared: true,
        useHTML: true,
        outside: true,
        backgroundColor: 'transparent',
        borderWidth: 0,
        shadow: false,
        padding: 0,
        style: { color: theme.textPrimary, fontSize: '12px' },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const fmt = (v: number | null | undefined) => (v == null ? 'N/A' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`)
          const box =
            `border:1px solid ${theme.border};border-radius:6px;padding:6px 10px;` +
            `background:${theme.cardBackground};box-shadow:0 2px 8px rgba(0,0,0,0.15)`
          const points = this.points ?? []
          const hovered = points[0]?.point.series.chart.hoverPoint
          const lines = points
            .map((p) => {
              const isHovered = p.point === hovered
              const label = displayFactorName(p.series.name)
              const name = isHovered ? `<b>${label}</b>` : label
              return `<div><span style="color:${p.series.color}">●</span> ${name} : <b>${fmt(p.y)}</b></div>`
            })
            .join('')
          const main =
            `<div style="${box}"><span style="font-size:12px;font-weight:600">${this.x}</span>` +
            `<div style="margin-top:4px">${lines}</div></div>`
          let side = ''
          if (hovered) {
            const custom = (hovered.options.custom ?? {}) as { benchmark?: number | null; active?: number | null }
            side =
              `<div style="align-self:center;${box}">` +
              `<div style="font-weight:600;margin-bottom:2px"><span style="color:${hovered.color as string}">●</span> ${displayFactorName(hovered.series.name)}</div>` +
              `<div>Benchmark : <b>${fmt(custom.benchmark)}</b></div>` +
              `<div>Active : <b>${fmt(custom.active)}</b></div></div>`
          }
          return `<div style="display:flex;gap:8px;align-items:flex-start">${main}${side}</div>`
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          states: { hover: { enabled: false } },
          point: {
            events: {
              // Click a factor to drill one level deeper (grouping only, dates unchanged). Only factors
              // that actually have a child level are drillable ("Other" and childless factors aren't).
              click: function (this: Highcharts.Point) {
                if (!drillableGroups.has(this.series.name)) return
                setDrillPath((prev) => [...prev, this.series.name])
              },
            },
          },
        },
      },
      series: series.map((s, i) => ({
        type: 'column',
        name: s.name,
        // Top-strip toggle hides top-level factors; drilled sub-factors always show.
        visible: drillPath.length ? true : !hiddenFactors.has(s.name),
        // Bars only look clickable when the factor can actually be drilled.
        cursor: drillableGroups.has(s.name) ? 'pointer' : 'default',
        // Portfolio is the plotted value; benchmark/active ride along per point for the tooltip.
        data: s.data.map((p) => ({ y: p.portfolio, custom: { benchmark: p.benchmark, active: p.active } })),
        // Undrilled bars share the strip's name→colour map; drilled levels index off their own order.
        color:
          drillPath.length === 0
            ? (topLevelColors.get(s.name) ?? FACTOR_COLORS[i % FACTOR_COLORS.length])
            : FACTOR_COLORS[i % FACTOR_COLORS.length],
      })),
    }),
    [
      theme,
      categories,
      series,
      drillPath,
      hiddenFactors,
      legendTotals,
      drillableGroups,
      topLevelColors,
      periods,
      setZoomPeriod,
    ],
  )

  return (
    <Stack
      sx={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        padding: '12px',
        boxSizing: 'border-box',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack
        direction='row'
        sx={{
          borderBottom: `1px solid ${theme.border}`,
          pb: '4px',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <Stack>
          <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: theme.textPrimary }}>{title}</Typography>
          {periodLabel && (
            <Typography sx={{ fontSize: '12px', color: theme.textSecondary, mt: '4px' }}>{periodLabel}</Typography>
          )}
          {zoomPeriod && (
            <Typography sx={{ fontSize: '12px', color: theme.textSecondary, mt: '2px' }}>
              Zoomed to {zoomPeriod.start.format('M/D/YYYY')} – {zoomPeriod.end.format('M/D/YYYY')}
            </Typography>
          )}
        </Stack>
        <Stack direction='row' sx={{ flexShrink: 0, gap: '4px', alignItems: 'center' }}>
          {view === 'table' && zoomPeriod && (
            <Box
              component='button'
              type='button'
              onClick={() => setZoomPeriod(null)}
              sx={{
                padding: '5px 10px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.cardBackground,
                color: theme.textSecondary,
                cursor: 'pointer',
                fontSize: '12px',
                whiteSpace: 'nowrap',
              }}
            >
              Reset zoom
            </Box>
          )}
          <Box
            component='button'
            type='button'
            aria-label='Chart view'
            sx={toggleButtonSx(view === 'chart')}
            onClick={() => {
              setView('chart')
              saveViewPrefs('chart', hiddenFactors)
            }}
          >
            <BarChartIcon sx={{ fontSize: '18px' }} />
          </Box>
          <Box
            component='button'
            type='button'
            aria-label='Table view'
            sx={toggleButtonSx(view === 'table')}
            onClick={() => {
              setView('table')
              saveViewPrefs('table', hiddenFactors)
            }}
          >
            <TableChartOutlinedIcon sx={{ fontSize: '18px' }} />
          </Box>
        </Stack>
      </Stack>
      {totals.length > 0 && (
        <Stack
          direction='row'
          sx={{
            flexWrap: 'wrap',
            gap: '2px 16px',
            mt: '8px',
            pb: '6px',
            borderBottom: `1px solid ${theme.border}`,
          }}
        >
          {totals.map((t, i) => {
            const v = t.portfolio
            const text = v == null ? 'N/A' : `${v.toFixed(2)}%`
            // Hover shows the full value; trim float noise from the summed total.
            const hover = v == null ? 'N/A' : `${Number(v.toPrecision(12))}%`
            // Toggle top-level factor visibility — only in the undrilled view (drilled chart is sub-factors).
            const interactive = drillPath.length === 0
            const hidden = interactive && hiddenFactors.has(t.name)
            const toggle = () => {
              const next = new Set(hiddenFactors)
              if (next.has(t.name)) next.delete(t.name)
              else next.add(t.name)
              setHiddenFactors(next)
              saveViewPrefs(view, next)
            }
            return (
              <Stack
                key={t.name}
                direction='row'
                component={interactive ? 'button' : 'div'}
                type={interactive ? 'button' : undefined}
                onClick={interactive ? toggle : undefined}
                title={interactive ? `${hover} — ${hidden ? 'Show' : 'Hide'} factor` : hover}
                sx={{
                  alignItems: 'center',
                  gap: '5px',
                  border: 0,
                  background: 'none',
                  p: 0,
                  font: 'inherit',
                  cursor: interactive ? 'pointer' : 'default',
                  opacity: hidden ? 0.4 : 1,
                  userSelect: 'none',
                }}
              >
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: topLevelColors.get(t.name) ?? FACTOR_COLORS[i % FACTOR_COLORS.length],
                  }}
                />
                <Typography
                  component='span'
                  sx={{
                    fontSize: '12px',
                    color: theme.textSecondary,
                    textDecoration: hidden ? 'line-through' : 'none',
                  }}
                >
                  {displayFactorName(t.name)} :
                </Typography>
                <Typography component='span' sx={{ fontSize: '12px', fontWeight: 600, color: theme.textPrimary }}>
                  {text}
                </Typography>
              </Stack>
            )
          })}
        </Stack>
      )}
      {view === 'chart' && drillPath.length > 0 && (
        <Stack direction='row' sx={{ alignItems: 'center', flexWrap: 'wrap', gap: '4px', mt: '8px' }}>
          <Typography component='button' type='button' onClick={() => setDrillPath([])} sx={crumbLinkSx}>
            Back to All Factors
          </Typography>
          {drillPath.map((name, i) => {
            const isLast = i === drillPath.length - 1
            return (
              <React.Fragment key={`${name}-${i}`}>
                <Typography sx={{ fontSize: '12px', color: theme.textSecondary }}>›</Typography>
                {isLast ? (
                  <Typography sx={{ fontSize: '12px', color: theme.textPrimary, fontWeight: 600 }}>
                    {displayFactorName(name)}
                  </Typography>
                ) : (
                  <Typography
                    component='button'
                    type='button'
                    onClick={() => setDrillPath(drillPath.slice(0, i + 1))}
                    sx={crumbLinkSx}
                  >
                    {displayFactorName(name)}
                  </Typography>
                )}
              </React.Fragment>
            )
          })}
        </Stack>
      )}
      {factorError ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: COMPANY_COLORS.ERROR }}>
            Couldn&apos;t load factor attribution data
          </Typography>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary, mt: '4px' }}>
            There was an error fetching the data. Please retry, or reach out to support if it persists.
          </Typography>
        </Stack>
      ) : (isLoading || zoomLoading) && !hasData ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={28} sx={{ color: COMPANY_COLORS.DARK_BLUE }} />
        </Stack>
      ) : view === 'table' ? (
        tableRows.length === 0 ? (
          <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>
              {zoomPeriod
                ? zoomError
                  ? "Couldn't load the zoomed range. Reset zoom to return to the full range."
                  : 'No factor attribution data for the zoomed range.'
                : 'Run analysis for the selected portfolio and date range to view factor attribution.'}
            </Typography>
          </Stack>
        ) : (
          <Box
            className={getGridThemeClass(isDarkMode)}
            sx={{
              flex: 1,
              minHeight: 0,
              mt: '8px',
              overflow: 'hidden',
              '--ag-secondary-foreground-color': theme.textSecondary,
              '--ag-font-size': '14px',
              '--ag-grid-size': '4px',
              '--ag-cell-horizontal-padding': '10px',
              '--ag-input-border-color': theme.border,
              // The group-header label is `flex:none` + sticky (pinned left); stretch it so it can center.
              '& .ag-header-group-cell-label': { justifyContent: 'center' },
              '& .ag-header-group-cell-label.ag-sticky-label': { flex: '1 1 auto' },
              '& .ag-header-group-text': { textAlign: 'center' },
              // Vertical dividers between the Factor Return / Exposure / Return Contribution groups.
              '& .ag-header-cell.fc-divider, & .ag-header-group-cell.fc-divider, & .ag-cell.fc-divider': {
                borderRight: `1px solid ${theme.border}`,
              },
              ...gridHeaderIconsSx,
            }}
          >
            <AgGridReact
              ref={factorTableGridRef}
              containerStyle={{ height: '100%', width: '100%' }}
              rowData={tableRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              treeData
              getDataPath={getDataPath}
              getContextMenuItems={getContextMenuItems}
              defaultCsvExportParams={defaultCsvExportParams}
              defaultExcelExportParams={defaultExcelExportParams}
              groupDefaultExpanded={-1}
              suppressAggFuncInHeader
              columnHoverHighlight
              enableBrowserTooltips
              onGridReady={onFactorTableGridReady}
              onColumnMoved={onFactorTableColumnMoved}
            />
          </Box>
        )
      ) : view === 'chart' && zoomPeriod && zoomError ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: COMPANY_COLORS.ERROR }}>
            Couldn&apos;t load the zoomed range
          </Typography>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>
            Reset zoom to return to the full range.
          </Typography>
          <Box
            component='button'
            type='button'
            onClick={() => setZoomPeriod(null)}
            sx={{
              padding: '5px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.cardBackground,
              color: theme.textSecondary,
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Reset zoom
          </Box>
        </Stack>
      ) : view === 'chart' && zoomPeriod && !hasData ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>
            No factor attribution data for the zoomed range.
          </Typography>
          <Box
            component='button'
            type='button'
            onClick={() => setZoomPeriod(null)}
            sx={{
              padding: '5px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.cardBackground,
              color: theme.textSecondary,
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Reset zoom
          </Box>
        </Stack>
      ) : drillPath.length > 0 && !hasData ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>
            No sub-factor data for this factor.
          </Typography>
        </Stack>
      ) : !hasData ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>
            Run analysis for the selected portfolio and date range to view factor attribution.
          </Typography>
        </Stack>
      ) : (
        <Stack sx={{ flex: 1, minHeight: 0, overflow: 'hidden', mt: '8px', position: 'relative' }}>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: '100%', width: '100%' } }}
          />
          {zoomPeriod && (
            <Box
              component='button'
              type='button'
              onClick={() => setZoomPeriod(null)}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 5,
                padding: '5px 10px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.cardBackground,
                color: theme.textSecondary,
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: COMPANY_COLORS.DARK_BLUE, color: COMPANY_COLORS.DARK_BLUE },
              }}
            >
              Reset zoom
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default FactorAttributionChart
